/**
 * Shape 매트릭스 — (sourceKind × relation) 2차원 그리드.
 *
 * 각 셀에는 현재 정의된 targetKind 들을 칩으로 표시하고, 마지막에 "+ targetKind 추가" 인라인 입력을 제공한다.
 * shapes 가 비어있으면 "모든 (kind, relation, kind) 조합이 허용됩니다" empty 상태.
 */
import { useEffect, useMemo, useRef, useState } from "react"
import type { KindDef, RelationDef, ShapeDef } from "../../api/admin-client.ts"

interface ShapeMatrixProps {
	readonly kinds: ReadonlyArray<KindDef>
	readonly relations: ReadonlyArray<RelationDef>
	readonly shapes: ReadonlyArray<ShapeDef>
	readonly onAdd: (shape: ShapeDef) => void
	readonly onRemove: (shape: ShapeDef) => void
}

interface CellKey {
	readonly sourceKind: string
	readonly relation: string
}

/** 같은 (source,relation) 에 묶인 target 목록을 모아둔 Map. */
function groupByCell(shapes: ReadonlyArray<ShapeDef>): Map<string, ShapeDef[]> {
	const map = new Map<string, ShapeDef[]>()
	for (const s of shapes) {
		const key = `${s.sourceKind}|${s.relation}`
		const list = map.get(key) ?? []
		list.push(s)
		map.set(key, list)
	}
	return map
}

export function ShapeMatrix(props: ShapeMatrixProps) {
	const { kinds, relations, shapes } = props
	const byCell = useMemo(() => groupByCell(shapes), [shapes])
	const [editing, setEditing] = useState<CellKey | null>(null)
	const [pendingTarget, setPendingTarget] = useState("")

	const hasAny = kinds.length > 0 && relations.length > 0

	return (
		<section className="ontology-section">
			<h3 className="ontology-section-title">
				Shapes <span className="muted">({shapes.length})</span>
			</h3>
			<p className="muted ontology-section-hint">
				(source kind) — [relation] → (target kind) 트리플. 비어있으면 모든 조합이 허용됩니다.
			</p>

			{!hasAny ? (
				<div className="empty" style={{ padding: 32 }}>
					<p>먼저 Kind 와 Relation 을 하나 이상 정의해야 Shape 을 만들 수 있습니다.</p>
				</div>
			) : (
				<div className="shape-matrix">
					<table>
						<thead>
							<tr>
								<th>소스 kind</th>
								{relations.map((r) => (
									<th key={r.name}>
										<code className="inline">{r.name}</code>
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{kinds.map((srcKind) => (
								<tr key={srcKind.name}>
									<td className="shape-matrix-rowhead">
										<code className="inline">{srcKind.name}</code>
									</td>
									{relations.map((rel) => {
										const cellKey = `${srcKind.name}|${rel.name}`
										const cellShapes = byCell.get(cellKey) ?? []
										const isEditing = editing?.sourceKind === srcKind.name && editing?.relation === rel.name
										return (
											<td key={rel.name} className="shape-matrix-cell">
												<div className="shape-matrix-cell-inner">
													{cellShapes.map((s) => (
														<span key={s.targetKind} className="chip shape-chip">
															{s.targetKind}
															<button
																type="button"
																className="chip-remove"
																aria-label={`${s.targetKind} 삭제`}
																onClick={() => props.onRemove(s)}
															>
																×
															</button>
														</span>
													))}
													{isEditing ? (
														<InlineTargetPicker
															kinds={kinds}
															value={pendingTarget}
															onChange={setPendingTarget}
															onCancel={() => {
																setEditing(null)
																setPendingTarget("")
															}}
															onConfirm={(target) => {
																if (target) {
																	props.onAdd({
																		sourceKind: srcKind.name,
																		relation: rel.name,
																		targetKind: target
																	})
																}
																setEditing(null)
																setPendingTarget("")
															}}
														/>
													) : (
														<button
															type="button"
															className="shape-matrix-add"
															onClick={() => {
																setEditing({
																	sourceKind: srcKind.name,
																	relation: rel.name
																})
																setPendingTarget("")
															}}
														>
															+
														</button>
													)}
												</div>
											</td>
										)
									})}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	)
}

function InlineTargetPicker(props: {
	readonly kinds: ReadonlyArray<KindDef>
	readonly value: string
	readonly onChange: (next: string) => void
	readonly onConfirm: (target: string) => void
	readonly onCancel: () => void
}) {
	const selectRef = useRef<HTMLSelectElement>(null)

	// 인라인 편집 열리는 즉시 피커로 포커스 이동 (a11y: autoFocus 속성 대신 effect 사용)
	useEffect(() => {
		selectRef.current?.focus()
	}, [])

	return (
		<span className="shape-matrix-picker">
			<select
				ref={selectRef}
				className="input select"
				value={props.value}
				onChange={(e) => props.onChange(e.target.value)}
			>
				<option value="">(대상 kind 선택)</option>
				{props.kinds.map((k) => (
					<option key={k.name} value={k.name}>
						{k.name}
					</option>
				))}
			</select>
			<button
				type="button"
				className="btn btn-primary btn-sm"
				onClick={() => props.onConfirm(props.value)}
				disabled={!props.value}
			>
				추가
			</button>
			<button type="button" className="btn btn-ghost btn-sm" onClick={props.onCancel}>
				취소
			</button>
		</span>
	)
}
